/*
 * Project: bellcloud
 * File: packages/utils/device/devicePacketUnified.ts
 * Author: SWAN
 * Created: 2025-01-30
 * Description: LITE/MINI 자동 감지 후 패킷 생성·파싱 (generateUnifiedPacket / parseUnifiedPacket).
 */

import { detectProtocol } from './detectBellProtocol';
import { jsonToPacket, packetToJson } from './devicePacketCodec';
import { getFunctionParameters as getLiteFunctionParameters } from '../../protocols/lite/fn_params';
import { getFunctionParameters as getMiniFunctionParameters } from '../../protocols/mini/fn_params';
import type { DeviceInfo } from '../../types/device/lite/deviceInfo';
import { DeviceType, type ProtocolType } from '../../types/device/common/deviceTypes';

export interface UnifiedPacketRequest {
  class_id: number;
  function_id: number;
  json_payload?: Record<string, unknown>;
  deviceInfo?: DeviceInfo;
  serialNumber?: string;
  deviceName?: string;
  protocol?: ProtocolType;
}

export interface UnifiedParseRequest {
  packet: number[];
  serialNumber?: string;
}

interface UnifiedPacketResponse {
  packet: number[];
  protocol: ProtocolType;
  deviceType: DeviceType;
}

interface UnifiedParseResponse {
  json: Record<string, unknown>;
  class_id: number;
  protocol: ProtocolType;
  deviceType: DeviceType;
  function_id: number;
}

function getFunctionParametersForProtocol(protocol: ProtocolType) {
  return protocol === DeviceType.MINI ? getMiniFunctionParameters : getLiteFunctionParameters;
}

/**
 * 통합 패킷 생성 (디바이스 타입 자동 감지)
 * @param request - Unified packet generation request
 * @returns Generated packet with protocol info
 */
export function generateUnifiedPacket(request: UnifiedPacketRequest): UnifiedPacketResponse {
  const { protocol: explicitProtocol, serialNumber } = request;

  let protocol: ProtocolType;
  let deviceType: DeviceType;

  if (explicitProtocol) {
    protocol = explicitProtocol;
    deviceType = explicitProtocol;
  } else {
    const detected = detectProtocol({ serialNumber });
    protocol = detected.protocol;
    deviceType = detected.deviceType;
  }

  const getFunctionParameters = getFunctionParametersForProtocol(protocol);
  const paramDefs = getFunctionParameters(request.class_id, request.function_id);

  const packet = jsonToPacket(
    request.class_id,
    request.function_id,
    request.json_payload || {},
    paramDefs,
    protocol,
    request.deviceInfo?.sensor_type ? { sensor_type: request.deviceInfo.sensor_type } : undefined
  );

  return { packet, protocol, deviceType };
}

/**
 * 통합 패킷 파싱 (디바이스 타입 자동 감지)
 * @param request - Unified packet parsing request
 * @returns Parsed JSON with protocol info
 */
export function parseUnifiedPacket(request: UnifiedParseRequest): UnifiedParseResponse {
  const { packet, serialNumber } = request;

  const { protocol, deviceType } = detectProtocol({ serialNumber, packet });

  const class_id = packet[4];
  const function_id = packet[5];

  const getFunctionParameters = getFunctionParametersForProtocol(protocol);
  const paramDefs = getFunctionParameters(class_id, function_id);

  const payload = packet.slice(8);
  const json = packetToJson(payload, paramDefs, protocol);

  return { json, protocol, deviceType, class_id, function_id };
}

/**
 * 프로토콜 검증 (호환성 확인)
 * @param protocol - Protocol type
 * @param class_id - Class ID
 * @param function_id - Function ID
 * @returns true if valid combination
 */
export function validateProtocolCommand(
  protocol: ProtocolType,
  class_id: number,
  function_id: number
): boolean {
  try {
    const getFunctionParameters = getFunctionParametersForProtocol(protocol);
    const paramDefs = getFunctionParameters(class_id, function_id);
    return paramDefs !== undefined;
  } catch {
    return false;
  }
}
